/**
 * LinearTimeline.tsx
 *
 * A flat, single-track step timeline injected into the DashboardLayout header
 * by the invitation and render wizards.
 *
 * Visual contract (mirrors WizardTimeline):
 *  - Steps BEFORE `currentStep` → green dot with checkmark (completed).
 *  - Step AT `currentStep`       → primary-coloured dot with glow (current).
 *  - Steps AFTER `currentStep`   → default dimmed dot (future / locked).
 *
 * Completed steps are clickable when `onStepClick` is provided; future steps
 * are never interactive.
 *
 * Responsive:
 *  - Labels hidden below 1100 px.
 *  - Dots shrink at 768 px and 480 px.
 */

import styles from './LinearTimeline.module.css';

// ─── Props ────────────────────────────────────────────────────────────────────

interface LinearTimelineProps {
  /**
   * Ordered array of step labels to display.
   * Example: ['City', 'Theme', 'Details', 'Preview', 'Generate']
   */
  steps: string[];

  /**
   * Zero-based index of the currently active step.
   * Steps with index < currentStep are rendered as completed.
   */
  currentStep: number;

  /**
   * Optional callback invoked when the user clicks a completed step.
   * The argument is the zero-based step index.
   * Future steps (index > currentStep) never trigger this callback.
   */
  onStepClick?: (stepIndex: number) => void;
}

// ─── Component ────────────────────────────────────────────────────────────────

/**
 * LinearTimeline — a single-track header step indicator.
 *
 * Renders `steps.length` step nodes connected by horizontal lines.
 * Completed steps (index < currentStep) are clickable if `onStepClick` is
 * provided, allowing the user to navigate backward through the wizard.
 */
export const LinearTimeline = ({
  steps,
  currentStep,
  onStepClick,
}: LinearTimelineProps) => {
  return (
    <div className={styles.timeline}>
      {steps.map((label, index) => {
        const isCompleted = index < currentStep;
        const isCurrent = index === currentStep;
        const isClickable = isCompleted && Boolean(onStepClick);

        const stepClasses = [
          styles.step,
          isCompleted ? styles.completed : '',
          isCurrent ? styles.current : '',
          isClickable ? styles.clickable : '',
        ]
          .filter(Boolean)
          .join(' ');

        return (
          <div key={label} className={styles.stepWrapper}>
            <button
              type="button"
              className={stepClasses}
              onClick={() => isClickable && onStepClick?.(index)}
              disabled={!isClickable}
              title={label}
              aria-current={isCurrent ? 'step' : undefined}
              aria-label={
                isCompleted
                  ? `${label} (completed${isClickable ? ', click to return' : ''})`
                  : isCurrent
                    ? `${label} (current step)`
                    : `${label} (not started)`
              }
            >
              <div className={styles.dot}>
                {isCompleted ? (
                  <span className={styles.check} aria-hidden>
                    &#10003;
                  </span>
                ) : null}
              </div>
              <span className={styles.label}>{label}</span>
            </button>

            {/* Connector line — rendered after every step except the last */}
            {index < steps.length - 1 && (
              <div
                className={[
                  styles.connector,
                  isCompleted ? styles.connectorDone : '',
                ]
                  .filter(Boolean)
                  .join(' ')}
                aria-hidden
              />
            )}
          </div>
        );
      })}
    </div>
  );
};
